import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { LoadingButton } from "@mui/lab";
import { Box, Button } from "@mui/material";
import { Contact, useGetContactLazyQuery, User } from "@repo/server-api";
import { RuleFactoryType, TabKeys } from "@repo/server-api/types";
import { useNotification, RuleComponent } from "@repo/shared/components";
import { useTranslation } from "next-i18next";
import { JSX, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { validErrorState } from "@repo/legacy/components/helper/Index";
import {
	useUpdateCompanyContact,
	useUpdatePersonContact,
} from "@repo/legacy/components/hooks/ContactMutationHooks";
import {
	useCreateUserLogin,
	useUpdateUserLogin,
} from "@repo/legacy/components/hooks/UserMutationHooks";
import usePersistentState from "@repo/legacy/store/usePersistentState";

import BillingInformation from "../../components/shared/additionalInformation/BillingInformation";
import GeneralInfo from "../../components/shared/projects/GeneralInfoContact";
import ContactPersons from "../../components/shared/sections/ContactPersons";
import Tabs from "../../components/shared/Tabs";

const Client = (props: { ruleFactory: RuleFactoryType }) => {
	const { t } = useTranslation();

	const navigate = useNavigate();
	const [contact, setContact] = usePersistentState("client", null);
	const [tabIndex, setTabIndex] = useState<number>(0);

	const { showNotification } = useNotification();

	const [updateCompanyContact] = useUpdateCompanyContact();
	const [updatePersonContact] = useUpdatePersonContact();

	const [updateUserLogin] = useUpdateUserLogin();
	const [createUserLogin] = useCreateUserLogin();

	const { id } = useParams<{ id: string }>();

	const [saveClicked, setSaveClicked] = useState<boolean>(false);

	const [errors, setErrors] = useState<any>({});

	const generalInfoRef = useRef<{
		validate: () => Promise<Record<string, boolean | object | string>>;
	}>(null);
	const paymentInformationRef = useRef<{
		validate: () => Promise<Record<string, boolean | object | string>>;
	}>(null);
	const externalWorkingConditionsRef = useRef<{
		validate: () => Promise<Record<string, boolean | object | string>>;
	}>(null);
	const salaryInformationRef = useRef<{
		validate: () => Promise<Record<string, boolean | object | string>>;
	}>(null);
	const loginRef = useRef<{
		validate: () => Promise<Record<string, boolean | object | string>>;
	}>(null);

	const [user, _setUser] = useState<User | null>(null);

	const [tabs, setTabs] = useState<{ label: string; key: TabKeys; content: JSX.Element }[]>([]);

	const [getContact] = useGetContactLazyQuery({
		variables: { id: id ?? "" },
		fetchPolicy: "no-cache",
		onCompleted(data) {
			if (data && data.node) {
				setContact(data.node as Contact);
			}
		},
	});

	useEffect(() => {
		if (id !== contact?.id) getContact();
	}, [id]);

	const validate = async () => {
		const localErrors: Record<string, boolean | object | string> = {};

		switch (tabs[tabIndex].key) {
			case TabKeys.GENERAL_INFORMATION:
				localErrors.generalInfo = (await generalInfoRef.current?.validate()) || {};
				break;
			case TabKeys.PAYMENT_INFORMATION:
				localErrors.paymentInformation = (await paymentInformationRef.current?.validate()) || {};
				break;
			case TabKeys.SALARY_INFORMATION:
				localErrors.salaryInformation = (await salaryInformationRef.current?.validate()) || {};
				break;
			case TabKeys.EXTERNAL_WORKING_CONDITIONS:
				localErrors.externalWorkingConditions =
					(await externalWorkingConditionsRef.current?.validate()) || {};
				break;
			case TabKeys.LOGIN:
				localErrors.login = (await loginRef.current?.validate()) || {};
				break;
			default:
				break;
		}

		setErrors(localErrors);

		return validErrorState(localErrors);
	};

	const handleSubmit = async () => {
		const isValid = await validate();
		if (isValid) {
			setSaveClicked(true);
			handleSave();
		}
	};

	const handleSubmitLogin = async () => {
		const isValid = await validate();
		if (isValid) {
			setSaveClicked(true);
			handleSaveLogin();
		}
	};

	const handleSaveLogin = async () => {
		if (user?.id) {
			updateUserLogin(user)
				.then((data: any) => {
					data.forEach((d: any) => {
						if (d.userChangeGroups) {
							if (d.userChangeGroups.errors) {
								d.userChangeGroups.errors.forEach((err: any) => {
									showNotification(err.message, "error");
								});
							}
							if (d.userChangeGroups.user) {
								showNotification(t("login.groupsChanged"), "success");
							}
						}
						if (d.userSetEnabled) {
							if (d.userSetEnabled.errors) {
								d.userSetEnabled.errors.forEach((err: any) => {
									showNotification(err.message, "error");
								});
							}
							if (d.userSetEnabled.user) {
								if (d.userSetEnabled.user.enabled) showNotification(t("login.enabled"), "success");
								else showNotification(t("login.disabled"), "success");
							}
						}
					});
					setSaveClicked(false);
				})
				.catch((error: any) => {
					showNotification(error.message, "error");
					setSaveClicked(false);
				});
		} else {
			createUserLogin(contact, user)
				.then((res: any) => {
					if (typeof res === "string") showNotification(t("login.userCreated"), "success");
					else showNotification(res.message, "error");
					setSaveClicked(false);
				})
				.catch((err) => {
					showNotification(err.message, "error");
					setSaveClicked(false);
				});
		}
	};

	const handleSave = async () => {
		if (contact?.isACompany) {
			updateCompanyContact(contact)
				.then((res) => {
					if (typeof res === "string") showNotification(t("company-updated"), "success");
					else showNotification(res.message, "error");
					setSaveClicked(false);
				})
				.catch((err) => {
					showNotification(err.message, "error");
					setSaveClicked(false);
				});
		} else {
			updatePersonContact(contact)
				.then((res) => {
					if (res.id) showNotification(t("person-updated"), "success");
					else showNotification(res.message, "error");
					setSaveClicked(false);
				})
				.catch((err) => {
					showNotification(err.message, "error");
					setSaveClicked(false);
				});
		}

		// TODO Fix reload from backend
		setTimeout(() => getContact(), 1000);
	};

	const handleCancel = () => {
		setTabIndex(0);
		setSaveClicked(false);
		setContact(null);
		navigate(-1);
	};

	const allTabs = useMemo(
		() => [
			{
				label: t("tab.general-information"),
				key: TabKeys.GENERAL_INFORMATION,
				content: (
					<GeneralInfo
						ref={generalInfoRef}
						errors={errors.generalInfo}
						setErrors={(value) =>
							setErrors({
								...errors,
								generalInfo: { ...errors.generalInfo, ...value },
							})
						}
						contact={contact}
						setContact={setContact}
						ruleFactory={props.ruleFactory}
					/>
				),
			},
			{
				label: t("tab.contact-persons"),
				key: TabKeys.CONTACT_PERSONS,
				content: (
					<ContactPersons
						contact={contact}
						setContact={setContact}
						ruleFactory={props.ruleFactory}
					/>
				),
			},
			{
				label: t("tab.billing-information"),
				key: TabKeys.BILLING_INFORMATION,
				content: (
					<BillingInformation
						contact={contact}
						setContact={setContact}
						ruleFactory={props.ruleFactory}
					/>
				),
			},
		],

		[t, contact, errors],
	);

	useEffect(() => {
		setTabs([
			...allTabs.filter((tab) =>
				props.ruleFactory.allowedTabsForContact(contact).includes(tab.key),
			),
		]);
	}, [errors, contact, t, allTabs]);

	return (
		<Box
			style={{
				minHeight: "90vh",
				width: "100%",
				display: "flex",
				flexDirection: "column",
			}}>
			<Box>
				<Button className="left" variant="outlined" onClick={handleCancel}>
					<ArrowBackIcon />
				</Button>
				<div color="text.primary" className="left purpleHeader" style={{ marginLeft: 20 }}>
					{contact?.displayName}
					<span className="lightGreyFont" style={{ marginLeft: 10, fontSize: "12px" }}>
						({contact?.displayContactType.map((x) => t(x.toLowerCase())).join(", ")})
					</span>
				</div>
			</Box>
			<Box sx={{ flex: 1 }}>
				<Tabs
					tabs={tabs}
					tabIndex={tabIndex}
					setTabIndex={setTabIndex}
					rulefactory={props.ruleFactory}
				/>
			</Box>
			{tabs[tabIndex] && (
				<Box sx={{ display: "flex", justifyContent: "flex-end", py: 2, pr: "30px" }}>
					<RuleComponent rule={props.ruleFactory.generalSaveButton(tabs[tabIndex].key)}>
						<LoadingButton
							loading={saveClicked}
							className={saveClicked ? "" : "blueButton"}
							variant="contained"
							color="secondary"
							onClick={handleSubmit}>
							{t("save")}
						</LoadingButton>
					</RuleComponent>
					<RuleComponent rule={props.ruleFactory.loginSaveButton(tabs[tabIndex].key)}>
						<LoadingButton
							loading={saveClicked}
							className={saveClicked ? "" : "blueButton"}
							variant="contained"
							color="secondary"
							onClick={handleSubmitLogin}>
							{t("save")}
						</LoadingButton>
					</RuleComponent>
				</Box>
			)}
		</Box>
	);
};

export default Client;
